import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Radio } from "lucide-react";

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-[calc(100vh-80px)] flex">
      <div className="hidden lg:flex flex-col justify-between bg-stone-900 text-stone-50 w-1/2 p-12 relative overflow-hidden">
        <div className="absolute inset-0 hidden-truth-stripes opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#E60000]" />
            <span className="font-serif-head text-xl">Hot Truth Archive</span>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-serif-head text-5xl leading-[1.05] mb-6">
            See what the headline<br /><span className="italic text-[#E60000]">won&apos;t tell you.</span>
          </h2>
          <p className="text-sm text-stone-300 leading-relaxed max-w-md">
            Every story comes with an AI-generated analysis of missing context, source bias,
            counter-narratives, and fact-check flags. Your archive · your investigation.
          </p>
        </div>
        <div className="relative font-mono-head text-[10px] uppercase tracking-[0.3em] text-stone-500">
          · Investigative · Archival · Independent ·
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="font-mono-head text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">
            // {subtitle}
          </div>
          <h1 className="font-serif-head text-4xl sm:text-5xl text-stone-900 mb-8">{title}</h1>
          {children}
          <div className="mt-6 font-mono-head text-xs uppercase tracking-widest text-stone-500">
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}

const fieldClass = "w-full bg-transparent border-b-2 border-stone-300 focus:border-stone-900 text-base py-3 px-0 focus:outline-none placeholder-stone-400 font-mono-head";
const labelClass = "block font-mono-head text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-1 mt-5";
const btnClass = "w-full mt-8 bg-stone-900 text-stone-50 font-mono-head text-sm uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#E60000] transition-colors flex items-center justify-center gap-2 disabled:opacity-60";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Enter the vault"
      subtitle="Authenticate"
      footer={<>No account? <Link to="/register" data-testid="link-register" className="text-stone-900 hover:text-[#E60000]">Register →</Link></>}
    >
      <form onSubmit={onSubmit} data-testid="login-form">
        <label className={labelClass}>Email</label>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className={fieldClass} placeholder="you@domain.com"
          data-testid="login-email-input"
        />
        <label className={labelClass}>Password</label>
        <input
          type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className={fieldClass} placeholder="••••••••"
          data-testid="login-password-input"
        />
        <button type="submit" className={btnClass} disabled={submitting} data-testid="login-submit-btn">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? "Authenticating…" : "Sign In"}
        </button>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      await register(name, email, password);
      toast.success("Welcome to the Archive");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Open a dossier"
      subtitle="Create account"
      footer={<>Already have an account? <Link to="/login" data-testid="link-login" className="text-stone-900 hover:text-[#E60000]">Sign in →</Link></>}
    >
      <form onSubmit={onSubmit} data-testid="register-form">
        <label className={labelClass}>Name</label>
        <input
          type="text" required value={name} onChange={(e) => setName(e.target.value)}
          className={fieldClass} placeholder="Your name"
          data-testid="register-name-input"
        />
        <label className={labelClass}>Email</label>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className={fieldClass} placeholder="you@domain.com"
          data-testid="register-email-input"
        />
        <label className={labelClass}>Password</label>
        <input
          type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className={fieldClass} placeholder="Min. 6 characters"
          data-testid="register-password-input"
        />
        <button type="submit" className={btnClass} disabled={submitting} data-testid="register-submit-btn">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? "Creating…" : "Create Account"}
        </button>
      </form>
    </AuthShell>
  );
}
